import { useEffect, useRef } from 'react';

/**
 * Custom hook for managing WebSocket connections with automatic reconnection
 * @param {Object} options - Configuration options
 * @param {string} options.url - WebSocket URL to connect to
 * @param {boolean} options.enabled - Whether the connection should be active
 * @param {Function} options.onMessage - Callback for incoming messages
 * @param {Function} options.onOpen - Optional callback when connection opens
 * @param {Function} options.onClose - Optional callback when connection closes
 * @param {Function} options.onError - Optional callback for errors
 * @param {number} options.maxReconnectAttempts - Maximum reconnection attempts (default: 10)
 * @param {number} options.baseReconnectDelay - Base delay for reconnection in ms (default: 1000)
 * @param {number} options.maxReconnectDelay - Maximum delay for reconnection in ms (default: 30000)
 * @param {number} options.connectionTimeout - Timeout for initial connection in ms (default: 10000)
 * @returns {Object} - { wsRef, isConnected, reconnectAttempts }
 */
export const useWebSocket = ({
  url,
  enabled = true,
  onMessage,
  onOpen,
  onClose,
  onError,
  maxReconnectAttempts = 10,
  baseReconnectDelay = 1000,
  maxReconnectDelay = 30000,
  connectionTimeout = 10000,
}) => {
  const wsRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const connectionTimeoutRef = useRef(null);

  const getReconnectDelay = (attempt) => {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (capped)
    return Math.min(
      baseReconnectDelay * Math.pow(2, attempt),
      maxReconnectDelay
    );
  };

  const cleanup = () => {
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const connect = () => {
    if (!isMountedRef.current || !enabled || !url) return;

    cleanup();

    // Set connection timeout
    connectionTimeoutRef.current = setTimeout(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
        console.error('[useWebSocket] Connection timed out');
        wsRef.current.close();
      }
    }, connectionTimeout);

    try {
      const ws = new WebSocket(url);

      ws.onopen = (event) => {
        cleanup();
        if (!isMountedRef.current) {
          ws.close();
          return;
        }

        reconnectAttemptsRef.current = 0;
        wsRef.current = ws;

        if (onOpen) {
          onOpen(event, ws);
        }
      };

      ws.onerror = (error) => {
        cleanup();
        console.error('[useWebSocket] Error:', error);

        if (onError) {
          onError(error);
        }
      };

      ws.onclose = (event) => {
        cleanup();
        wsRef.current = null;

        if (onClose) {
          onClose(event);
        }

        // Attempt to reconnect if not a normal closure and component is still mounted
        if (isMountedRef.current && enabled && event.code !== 1000) {
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            const delay = getReconnectDelay(reconnectAttemptsRef.current);

            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectAttemptsRef.current++;
              connect();
            }, delay);
          } else {
            console.error(
              '[useWebSocket] Max reconnection attempts reached. Please refresh the page.'
            );
          }
        }
      };

      ws.onmessage = (event) => {
        if (!isMountedRef.current) return;

        if (onMessage) {
          onMessage(event, ws);
        }
      };

      wsRef.current = ws;
    } catch (error) {
      cleanup();
      console.error('[useWebSocket] Failed to initialize:', error);

      // Attempt reconnection on initialization failure
      if (
        isMountedRef.current &&
        enabled &&
        reconnectAttemptsRef.current < maxReconnectAttempts
      ) {
        const delay = getReconnectDelay(reconnectAttemptsRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connect();
        }, delay);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;

    // Only connect if we don't have an active or connecting WebSocket
    const shouldConnect =
      enabled &&
      url &&
      (!wsRef.current ||
        wsRef.current.readyState === WebSocket.CLOSED ||
        wsRef.current.readyState === WebSocket.CLOSING);

    if (shouldConnect) {
      connect();
    }

    // Handle page visibility changes to maintain connection
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page became visible - check connection health
        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      cleanup();
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (
        wsRef.current &&
        (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING)
      ) {
        wsRef.current.close(1000, 'Component unmounting');
      }
      wsRef.current = null;
    };
  }, [url, enabled]);

  return {
    wsRef,
    reconnectAttempts: reconnectAttemptsRef.current,
  };
};
